import React, { useState, useMemo } from 'react';

export interface ColumnDefinition<T = any> {
  key: string;
  header: string;
  width?: string;
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, row: T, index: number) => React.ReactNode;
  className?: string;
  headerClassName?: string;
}

export interface DataTableProps<T = any> {
  data: T[];
  columns: ColumnDefinition<T>[];
  keyField: string;
  className?: string;
  showFooter?: boolean;
  footerData?: any;
  onRowClick?: (row: T, index: number) => void;
  onRowSelect?: (selectedRows: T[]) => void;
  selectable?: boolean;
  loading?: boolean;
  emptyMessage?: string;
  rowClassName?: (row: T, index: number) => string;
  maxHeight?: string;
  stickyHeader?: boolean;
  sortable?: boolean;
  initialSort?: { column: string; direction: 'asc' | 'desc' };
}

export function DataTable<T = any>({
  data,
  columns,
  keyField,
  className = '',
  showFooter = false,
  footerData,
  onRowClick,
  onRowSelect,
  selectable = false,
  loading = false,
  emptyMessage = '데이터가 없습니다',
  rowClassName,
  maxHeight,
  stickyHeader = false,
  sortable = false,
  initialSort
}: DataTableProps<T>) {
  const [selectedRows, setSelectedRows] = useState<Set<any>>(new Set());
  const [sortConfig, setSortConfig] = useState<{ column: string; direction: 'asc' | 'desc' } | null>(
    initialSort || null
  );
  
  // 정렬된 데이터
  const sortedData = useMemo(() => {
    if (!sortConfig || !sortable) return data;
    
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.column];
      const bValue = b[sortConfig.column];
      
      if (aValue === bValue) return 0;
      
      const modifier = sortConfig.direction === 'asc' ? 1 : -1;
      
      // 숫자 비교
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * modifier;
      }
      
      // 문자열 비교
      return String(aValue).localeCompare(String(bValue)) * modifier;
    });
  }, [data, sortConfig, sortable]);
  
  // 정렬 처리
  const handleSort = (column: string) => {
    if (!sortable) return;
    
    setSortConfig(current => {
      if (current?.column === column) {
        return current.direction === 'asc' 
          ? { column, direction: 'desc' }
          : null;
      }
      return { column, direction: 'asc' };
    });
  };
  
  // 행 선택 처리
  const handleRowSelect = (rowKey: any, selected: boolean) => {
    const newSelected = new Set(selectedRows);
    if (selected) {
      newSelected.add(rowKey);
    } else {
      newSelected.delete(rowKey);
    }
    setSelectedRows(newSelected);
    
    if (onRowSelect) {
      const selectedData = data.filter(row => newSelected.has(row[keyField]));
      onRowSelect(selectedData);
    }
  };
  
  // 전체 선택 처리
  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      const allKeys = data.map(row => row[keyField]);
      setSelectedRows(new Set(allKeys));
      onRowSelect?.(data);
    } else {
      setSelectedRows(new Set());
      onRowSelect?.([]);
    }
  };
  
  const isAllSelected = data.length > 0 && selectedRows.size === data.length;
  const isPartiallySelected = selectedRows.size > 0 && selectedRows.size < data.length;
  
  // 셀 값 렌더링
  const renderCell = (column: ColumnDefinition<T>, row: T, index: number) => {
    const value = row[column.key];
    
    if (column.render) {
      return column.render(value, row, index);
    }
    
    // 숫자인 경우 천 단위 콤마 적용
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    
    return value;
  };
  
  const tableClasses = `
    w-full border-collapse bg-white rounded-lg shadow-sm overflow-hidden
    ${stickyHeader ? 'relative' : ''}
    ${className}
  `;
  
  const containerClasses = `
    ${maxHeight ? `overflow-auto` : ''}
    ${maxHeight ? `max-h-[${maxHeight}]` : ''}
  `;
  
  return (
    <div className={containerClasses}>
      <table className={tableClasses}>
        <thead className={`${stickyHeader ? 'sticky top-0 z-10' : ''} bg-gray-50`}>
          <tr>
            {selectable && (
              <th className="w-12 px-4 py-3 text-center border-b border-gray-200">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={input => {
                    if (input) input.indeterminate = isPartiallySelected;
                  }}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
            )}
            {columns.map((column) => {
              const isSorted = sortConfig?.column === column.key;
              const sortDirection = isSorted ? sortConfig!.direction : null;
              
              return (
                <th
                  key={column.key}
                  className={`
                    px-4 py-3 border-b border-gray-200 font-medium text-gray-900
                    ${column.align === 'center' ? 'text-center' : ''}
                    ${column.align === 'right' ? 'text-right' : ''}
                    ${column.align === 'left' || !column.align ? 'text-left' : ''}
                    ${column.sortable && sortable ? 'cursor-pointer hover:bg-gray-100 select-none' : ''}
                    ${column.width ? `w-[${column.width}]` : ''}
                    ${column.headerClassName || ''}
                  `}
                  onClick={() => column.sortable && handleSort(column.key)}
                  style={column.width ? { width: column.width } : undefined}
                >
                  <div className="flex items-center justify-between">
                    <span>{column.header}</span>
                                         {column.sortable && sortable && (
                       <div className="ml-2 flex flex-col">
                         <span 
                           className={`text-xs ${
                             isSorted && sortDirection === 'asc' 
                               ? 'text-blue-600' 
                               : 'text-gray-400'
                           }`}
                         >
                           ▲
                         </span>
                         <span 
                           className={`text-xs -mt-1 ${
                             isSorted && sortDirection === 'desc' 
                               ? 'text-blue-600' 
                               : 'text-gray-400'
                           }`}
                         >
                           ▼
                         </span>
                       </div>
                     )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-4 py-8 text-center">
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <span className="text-gray-500">데이터를 불러오는 중...</span>
                </div>
              </td>
            </tr>
          ) : sortedData.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-4 py-8 text-center text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            sortedData.map((row, index) => {
              const rowKey = row[keyField];
              const isSelected = selectedRows.has(rowKey);
              const customRowClass = rowClassName ? rowClassName(row, index) : '';
              
              return (
                <tr
                  key={rowKey || index}
                  className={`
                    border-b border-gray-100 hover:bg-gray-50 transition-colors
                    ${isSelected ? 'bg-blue-50' : ''}
                    ${onRowClick ? 'cursor-pointer' : ''}
                    ${customRowClass}
                  `}
                  onClick={() => onRowClick?.(row, index)}
                >
                  {selectable && (
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => handleRowSelect(rowKey, e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`
                        px-4 py-3 text-sm
                        ${column.align === 'center' ? 'text-center' : ''}
                        ${column.align === 'right' ? 'text-right' : ''}
                        ${column.align === 'left' || !column.align ? 'text-left' : ''}
                        ${column.className || ''}
                      `}
                    >
                      {renderCell(column, row, index)}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
        {showFooter && footerData && (
          <tfoot className="bg-gray-50">
            <tr>
              {selectable && <td className="px-4 py-3"></td>}
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={`
                    px-4 py-3 font-semibold text-sm border-t border-gray-200
                    ${column.align === 'center' ? 'text-center' : ''}
                    ${column.align === 'right' ? 'text-right' : ''}
                    ${column.align === 'left' || !column.align ? 'text-left' : ''}
                  `}
                >
                  {footerData[column.key] !== undefined ? footerData[column.key] : ''}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

export default DataTable; 